/**
 * Fundamentals — the world outside the chart, on one axis.
 *
 * ## One spine, not three sections
 *
 * This screen used to be a calendar, then a risk reading, then a feed. A trader
 * reading it held three orderings in their head and merged them to answer one
 * question: *what is happening, and in what order*. It is now a single
 * chronological spine — scheduled releases running forward from now, published
 * releases and headlines running back from it, separated by a marker rather
 * than by a heading.
 *
 * Above it sits one instrument: risk appetite as a ribbon with headline volume
 * as a histogram on the SAME time axis, so a spike in the news sits under the
 * bend it caused.
 *
 * ## The two things this screen refuses to do
 *
 * **It does not print a forecast it does not have.** Consensus is a survey and
 * every source for it is commercial — Finnhub's calendar returned `403` on the
 * free tier when it was checked. So the slot says so, rather than showing the
 * prior figure under a heading that reads "expected". A print landing on last
 * month's number means nothing if the market expected a jump.
 *
 * **It does not name the regime.** The ribbon shows where the reading has been
 * and the coherence behind it; it never says "RISK ON" as a state of the world.
 * Describing positions is a fact, naming the regime is a judgement, and AEGIS is
 * not a registered investment adviser — Constraint 4.
 */
import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import {
  sessionWindow, newsPulse, peakCount, appetiteTrend,
  spineCountdown as countdown, agoLabel, periodLabel, buildTimeline, countBy,
  type TimelineRow,
} from '@aegis/core';
import { EXCHANGE } from '../lib/zone.js';
import {
  api, ApiError,
  type FeedEntry, type NewsFeedResponse, type ReleaseCard,
  type SentimentReading, type AppetitePoint,
  type ScoreTerm, type NewsItem,
} from '../lib/api.js';

interface Upcoming {
  sourceId: string;
  title: string;
  at: string;
  expecting: string | null;
  importance: 'critical' | 'high' | 'normal';
  affects: string[];
  unit: string | null;
  scheduling: 'calendar' | 'rule';
  enabled: boolean;
}

interface CalendarResponse {
  upcoming: Upcoming[];
  recent: ReleaseCard[];
}

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------

/**
 * A countdown, from an absolute instant.
 *
 * Never re-derived from "08:30 ET" in the browser. The schedule is stored as
 * wall-clock in a named zone precisely so daylight saving is resolved once, on
 * the server; recomputing it here would reintroduce the hour of drift that
 * design exists to prevent.
 */
/**
 * The trader's own zone, not the publisher's. A London user's CPI is at 13:30.
 *
 * The `timezone` preference is not always an IANA zone — its default is the
 * sentinel `'exchange'`, meaning "each contract in its own exchange's zone".
 * `lib/zone.ts` documents that trap and this walked into it: `Intl` threw on the
 * sentinel and the catch produced raw ISO strings on every row.
 *
 * A macro release has no exchange. CPI is published by a statistical agency, so
 * under that preference there is no per-row zone to resolve and the honest
 * answer is the reader's own — labelled, never silently substituted.
 */
function zoneOf(preference: string | null): string | undefined {
  if (!preference || preference === EXCHANGE) return undefined;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: preference });
    return preference;
  } catch {
    return undefined;
  }
}

/** What the times on this screen are in, for the header to say out loud. */
function zoneLabel(zone: string | undefined): string {
  return zone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'your local time';
}

function localTime(iso: string, zone: string | undefined): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false,
    ...(zone ? { timeZone: zone } : {}),
  }).format(at);
}

/** `2026-08` reads as `August`; a week-ending date reads as itself. */
// ---------------------------------------------------------------------------
// The session instrument — ribbon and pulse on ONE axis
// ---------------------------------------------------------------------------

/**
 * How much of the session both charts draw.
 *
 * The API retains four hours; the screen shows three. Asking for slightly less
 * than is kept means the left edge is a real edge rather than whatever the
 * buffer happened to still hold.
 */
const SESSION_MS = 3 * 60 * 60_000;

/** Enough to show shape at phone width without drawing sub-pixel bars. */
const PULSE_BUCKETS = 48;

const RIBBON_W = 800;
const RIBBON_H = 76;
const PULSE_H = 34;

/**
 * The appetite series as an area ribbon, diverging from a zero line.
 *
 * ## Gaps are drawn as gaps
 *
 * A null appetite means the feed stopped being trustworthy for that minute.
 * The path is broken there — `M` rather than `L` — instead of joining across,
 * because a continuous line over a hole asserts a steadiness that was never
 * measured. Same rule as the excluded legs below it: the display must not be
 * able to imply something the data does not support.
 */
function ribbonPath(points: readonly AppetitePoint[], span: number): string {
  if (points.length === 0) return '';
  const first = points[0]?.atMs ?? 0;
  const last = points[points.length - 1]?.atMs ?? first;
  const width = Math.max(1, last - first);
  const mid = RIBBON_H / 2;

  let d = '';
  let pen = false;
  for (const p of points) {
    if (p.appetite === null) { pen = false; continue; }
    const x = ((p.atMs - first) / width) * RIBBON_W;
    const y = mid - (p.appetite / span) * (mid - 4);
    d += `${pen ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`;
    pen = true;
  }
  return d;
}

function SessionInstrument(
  { reading, feed, zone }:
  { reading: SentimentReading | null; feed: NewsFeedResponse | null; zone: string | undefined },
): JSX.Element {
  // One clock for both charts. Two `Date.now()` calls would put the ribbon and
  // the histogram on axes separated by however long the render took.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => { setNow(Date.now()); }, 15_000);
    return () => { clearInterval(t); };
  }, []);

  const history = reading?.history ?? [];

  /* The shared axis. Both series are bucketed against THIS, which is the whole
     point of stacking them: a spike in the histogram sitting under a bend in
     the ribbon says "the news moved the market". Two charts that each decide
     their own start time drift apart and the alignment becomes decorative. */
  const window = useMemo(
    () => sessionWindow(new Date(now), SESSION_MS, PULSE_BUCKETS),
    [now],
  );

  const buckets = useMemo(() => {
    const entries = (feed?.entries ?? []).map((e) => (
      e.kind === 'release'
        ? { publishedAt: e.card.releasedAt, importance: e.card.importance }
        : { publishedAt: e.item.publishedAt, importance: e.item.importance }
    ));
    return newsPulse(entries, window);
  }, [feed, window]);

  const peak = peakCount(buckets);
  const inWindow = history.filter((p) => p.atMs >= window.startMs);
  const trend = appetiteTrend(inWindow);
  const readable = inWindow.filter((p) => p.appetite !== null);
  /* Headroom above the largest reading, so the ribbon is never pinned flat to
     its own edge.

     `Math.max(1, maxAbs)` normalised the series against its own maximum, which
     makes the largest reading land at exactly ±1 — the top or bottom edge of
     the band — by construction. A session that sat at −1.14 the whole time drew
     a straight line along the bottom and filled the half below it solid: a
     chart that had reduced itself to a coloured block, at the one moment a
     trader most wants to see whether risk is turning.

     The 1.35 multiplier keeps the largest reading at ~74% of the half-band, so
     there is visible room both above and below it. The 1.5 floor stops a very
     quiet session from magnifying noise into a dramatic-looking swing. */
  const maxAbs = Math.max(0, ...readable.map((p) => Math.abs(p.appetite ?? 0)));
  const span = Math.max(1.5, maxAbs * 1.35);
  const path = ribbonPath(inWindow, span);

  const appetite = reading?.appetite ?? null;
  const coherence = reading?.coherence ?? null;
  const incoherent = coherence !== null && coherence < 0.7;
  const up = (appetite ?? 0) >= 0;

  /* The verdict line. Never states a regime — describing where instruments sit
     is a fact, naming the regime is a judgement (Constraint 4). "Broadening"
     and "narrowing" describe the AGREEMENT between legs, not the market. */
  const verdict = appetite === null
    ? 'No reading'
    : incoherent
      ? 'Legs disagree — no clean read'
      : `${up ? 'Risk-on' : 'Risk-off'}${trend === null ? '' : trend === 'rising' ? ', and rising' : ', and falling'}`;

  return (
    <section className="fund-instrument" aria-label="Risk appetite and headline volume">
      <div className="section-head">
        <h3 className="fund-sub">Risk appetite &amp; headline volume · session</h3>
        <span className="fund-tf">
          {reading ? `${reading.timeframe} move ÷ its own volatility` : ''}
        </span>
      </div>

      <div className={`fund-verdict${incoherent ? ' weak' : up ? ' on' : ' off'}`}>{verdict}</div>
      <div className="fund-verdict-sub">
        {appetite === null
          ? 'Not enough trustworthy instruments to read.'
          : `${up ? '+' : ''}${appetite.toFixed(2)} mean · ${reading?.counted ?? 0} counted`}
      </div>

      {readable.length < 2 ? (
        /* Honest about its own youth, and about a feed it cannot read.

           This counted `inWindow`, which is every sample including the ones
           whose appetite is null — and null is precisely the state the API
           reports when no instrument clears the trust bar. So a production
           deployment with no broker credentials collected samples, passed a
           length check, and drew an empty axis with no line in it: a chart
           saying nothing, next to a verdict already saying "not enough
           trustworthy instruments". Seen on a phone.

           `readable` is the samples that carry a number, which is what the
           ribbon actually needs two of. */
        <p className="dim fund-ribbon-empty">
          {readable.length === 0 && inWindow.length >= 2
            ? 'No readable session yet — the ribbon draws once instruments clear the trust bar.'
            : 'Building the session picture — the ribbon fills in as readings are taken.'}
        </p>
      ) : (
        <svg
          className="fund-ribbon" viewBox={`0 0 ${RIBBON_W} ${RIBBON_H}`}
          preserveAspectRatio="none" role="img"
          aria-label={`Risk appetite over the session: ${verdict}`}
        >
          <defs>
            <linearGradient id="fund-up" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" className="fund-grad-up-a" />
              <stop offset="100%" className="fund-grad-up-b" />
            </linearGradient>
            <linearGradient id="fund-down" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" className="fund-grad-down-a" />
              <stop offset="100%" className="fund-grad-down-b" />
            </linearGradient>
            <clipPath id="fund-clip-up">
              <rect x="0" y="0" width={RIBBON_W} height={RIBBON_H / 2} />
            </clipPath>
            <clipPath id="fund-clip-down">
              <rect x="0" y={RIBBON_H / 2} width={RIBBON_W} height={RIBBON_H / 2} />
            </clipPath>
          </defs>
          {path ? (
            <>
              <path
                d={`${path}L${RIBBON_W},${RIBBON_H / 2}L0,${RIBBON_H / 2}Z`}
                fill="url(#fund-up)" clipPath="url(#fund-clip-up)"
              />
              <path
                d={`${path}L${RIBBON_W},${RIBBON_H / 2}L0,${RIBBON_H / 2}Z`}
                fill="url(#fund-down)" clipPath="url(#fund-clip-down)"
              />
            </>
          ) : null}
          <line
            className="fund-zero" x1="0" y1={RIBBON_H / 2} x2={RIBBON_W} y2={RIBBON_H / 2}
          />
          <path className={`fund-ribbon-line ${up ? 'on' : 'off'}`} d={path} />
        </svg>
      )}

      {peak > 0 ? (
        <svg
          className="fund-pulse" viewBox={`0 0 ${RIBBON_W} ${PULSE_H}`}
          preserveAspectRatio="none" role="img"
          aria-label={`Headline volume: busiest period had ${peak} stories`}
        >
          {buckets.map((b) => {
            const w = RIBBON_W / buckets.length;
            const x = ((b.startMs - window.startMs) / SESSION_MS) * RIBBON_W;
            // Full height of the band, not two thirds of it. A histogram that
            // never reaches its own ceiling wastes the space it reserved and
            // makes a busy minute look like a quiet one.
            const h = (b.count / peak) * (PULSE_H - 12);
            return (
              <rect
                key={b.startMs} className={`fund-pulse-bar imp-${b.peak ?? 'none'}`}
                x={x + 0.6} y={PULSE_H - 10 - h} width={Math.max(0.5, w - 1.2)} height={h} rx="1.2"
              >
                <title>{`${b.count} ${b.count === 1 ? 'story' : 'stories'}`}</title>
              </rect>
            );
          })}
          <line className="fund-pulse-axis" x1="0" y1={PULSE_H - 10} x2={RIBBON_W} y2={PULSE_H - 10} />
        </svg>
      ) : null}

      <div className="fund-axis-labels">
        <span>{localTime(new Date(window.startMs).toISOString(), zone)}</span>
        <span className="fund-axis-mid">headline volume</span>
        <span>now</span>
      </div>
    </section>
  );
}
// ---------------------------------------------------------------------------
// The feed
// ---------------------------------------------------------------------------

/**
 * Why this entry is where it is, as a stacked bar of its named terms.
 *
 * Collapsed by default. The terms come from the API already decomposed and
 * already summing to the score, so this draws them rather than deriving
 * anything — a breakdown computed here would be a second opinion about an
 * order this screen did not decide.
 */
function WhyHere({ why, score }: { why: ScoreTerm[]; score: number }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (why.length === 0) return null;

  // Widths run on magnitude; the sign only picks the colour. A penalty is a
  // real contribution and hiding it would make the parts stop adding up.
  const total = why.reduce((a, t) => a + Math.abs(t.value), 0) || 1;

  return (
    <div className="fund-why">
      <button
        type="button" className="fund-why-toggle" aria-expanded={open}
        onClick={() => { setOpen((o) => !o); }}
      >
        {open ? '▾' : '▸'} why this is here
      </button>
      {open ? (
        <div className="fund-why-body">
          <div className="fund-why-label">ranking score {score.toFixed(2)}</div>
          <div className="fund-why-bar">
            {why.map((t) => (
              <span
                key={t.id} className={`fund-why-seg t-${t.id}${t.value < 0 ? ' neg' : ''}`}
                style={{ flexGrow: Math.abs(t.value) / total }}
              >
                {Math.abs(t.value) / total > 0.16 ? t.label : ''}
              </span>
            ))}
          </div>
          <ul className="fund-why-legend">
            {why.map((t) => (
              <li key={t.id}>
                <i className={`t-${t.id}${t.value < 0 ? ' neg' : ''}`} aria-hidden="true" />
                {t.label} {t.value > 0 ? '+' : ''}{t.value.toFixed(2)}
                {t.detail ? <span className="dim"> · {t.detail}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The spine
// ---------------------------------------------------------------------------

/** What the timeline draws: a scheduled release, a print, or a headline. */
type SpineEntry =
  | { kind: 'upcoming'; at: string; importance: ReleaseImportance; affects: string[];
      sourceId: string; up: Upcoming }
  | { kind: 'release'; at: string; importance: ReleaseImportance; affects: string[];
      sourceId: string; card: ReleaseCard; score?: number; why?: ScoreTerm[] }
  | { kind: 'item'; at: string; importance: ReleaseImportance; affects: string[];
      sourceId: string; alsoCarriedBy: string[]; item: NewsItem;
      score?: number; why?: ScoreTerm[] };

type ReleaseImportance = 'critical' | 'high' | 'normal';

/**
 * Fold the calendar and the feed into the one shape the spine sorts.
 *
 * Done here rather than in `buildTimeline` because the mapping is about THIS
 * screen's two data sources; the ordering rule is general and lives in core.
 */
function spineEntries(cal: CalendarResponse | null, feed: NewsFeedResponse | null): SpineEntry[] {
  const out: SpineEntry[] = [];

  for (const u of cal?.upcoming ?? []) {
    out.push({
      kind: 'upcoming', at: u.at, importance: u.importance,
      affects: u.affects, sourceId: u.sourceId, up: u,
    });
  }

  for (const e of feed?.entries ?? []) {
    if (e.kind === 'release') {
      out.push({
        kind: 'release', at: e.card.releasedAt, importance: e.card.importance,
        affects: e.card.affects ?? [], sourceId: e.card.sourceId,
        card: e.card, score: e.score, why: e.why,
      });
    } else {
      out.push({
        kind: 'item', at: e.item.publishedAt, importance: e.item.importance,
        affects: e.item.affects, sourceId: e.item.sourceId,
        alsoCarriedBy: e.item.alsoCarriedBy, item: e.item,
        score: e.score, why: e.why,
      });
    }
  }

  return out;
}

/** `in 5d 12h` for something scheduled, `2h ago` / a clock time for something past. */
function stamp(row: TimelineRow<SpineEntry>, now: number, zone: string | undefined): string {
  if (row.side === 'scheduled') return `in ${countdown(row.atMs - now)}`;
  const rel = agoLabel(now - row.atMs);
  if (rel !== null) return rel;
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit', minute: '2-digit', hour12: false,
    ...(zone ? { timeZone: zone } : {}),
  }).format(new Date(row.atMs));
}

/** Overlapping source discs — corroboration, legible before the headline is read. */
function Pips({ sources }: { sources: string[] }): JSX.Element {
  return (
    <span className="fund-pips" aria-hidden="true">
      {sources.slice(0, 4).map((s, i) => (
        <span key={s} className={`fund-pip p${(i % 4) + 1}`}>{s.slice(0, 2).toUpperCase()}</span>
      ))}
    </span>
  );
}

/** The affected instruments, with the ones the caller trades marked. */
function Chips({ affects, traded }: { affects: string[]; traded: Set<string> }): JSX.Element | null {
  if (affects.length === 0) return null;
  return (
    <div className="fund-chips">
      {affects.slice(0, 6).map((a) => (
        <span key={a} className={`fund-chip${traded.has(a.toUpperCase()) ? ' own' : ''}`}>{a}</span>
      ))}
    </div>
  );
}

/**
 * One row on the spine.
 *
 * Every kind draws the same skeleton — timestamp in the gutter, source, title,
 * then whatever that kind has to add. Three separate row components was the
 * first attempt and it drifted immediately: the release row grew a chip style
 * the headline row did not have, for no reason anyone could state.
 */
function SpineRow(
  { row, now, zone, traded }:
  { row: TimelineRow<SpineEntry>; now: number; zone: string | undefined; traded: Set<string> },
): JSX.Element {
  const [open, setOpen] = useState(false);
  const e = row.entry;
  const cluster = row.carriedWith.length;

  const cls = [
    'fund-row',
    `imp-${e.importance}`,
    row.side === 'scheduled' ? 'sched' : 'pub',
    row.mine ? 'mine' : '',
    e.kind === 'item' ? 'news' : '',
    cluster > 0 ? 'clustered' : '',
    open ? 'open' : '',
  ].filter(Boolean).join(' ');

  /* A cluster is drawn as ONE line that expands, not as N rows. Six outlets
     carrying the Fed minutes used to bury every other entry under near-identical
     restatements of one event. */
  if (cluster > 0 && e.kind === 'item') {
    const sources = [e.sourceId, ...row.carriedWith.map((c) => c.sourceId)];
    return (
      <li className={cls}>
        <span className="fund-when">{stamp(row, now, zone)}</span>
        <button
          type="button" className="fund-cluster-head" aria-expanded={open}
          onClick={() => { setOpen((o) => !o); }}
        >
          <span className="fund-cluster-count">{cluster + 1}</span>
          <span className="fund-title news">{e.item.title}</span>
          <span className="fund-chev" aria-hidden="true">▸</span>
        </button>
        <div className="fund-cluster-body">
          {[e, ...row.carriedWith].map((c) => (
            <div key={c.kind === 'item' ? c.item.id : c.sourceId} className="fund-cluster-item">
              <span className="fund-src">{c.sourceId}</span>
              {c.kind === 'item' ? (
                <a href={c.item.url} target="_blank" rel="noreferrer noopener">{c.item.title}</a>
              ) : null}
            </div>
          ))}
        </div>
        <div className="fund-meta">
          <Pips sources={sources} />
          <span className="fund-corro-text">{cluster + 1} sources carry this</span>
        </div>
      </li>
    );
  }

  return (
    <li className={cls}>
      <span className="fund-when">{stamp(row, now, zone)}</span>
      <span className="fund-src">
        {e.sourceId}{e.kind === 'upcoming' ? ' · scheduled' : e.kind === 'release' ? ' · print' : ''}
      </span>

      {e.kind === 'item' ? (
        <a className="fund-title news" href={e.item.url} target="_blank" rel="noreferrer noopener">
          {e.item.title}
        </a>
      ) : (
        <span className="fund-title">
          {e.kind === 'upcoming' ? e.up.title : e.card.title}
          {e.kind === 'upcoming' && e.up.expecting
            ? <span className="fund-period"> · {periodLabel(e.up.expecting)}</span> : null}
          {e.kind === 'release' && e.card.referencePeriod
            ? <span className="fund-period"> · {periodLabel(e.card.referencePeriod)}</span> : null}
        </span>
      )}

      {e.kind === 'upcoming' ? (
        <div className="fund-print">
          <span className="fund-countdown">{countdown(row.atMs - now)}</span>
          <span className="fund-verdict-badge pending">AWAITING</span>
          <span className="fund-vs">{localTime(e.up.at, zone)}</span>
        </div>
      ) : null}

      {e.kind === 'release' && e.card.actual !== null ? (
        <div className="fund-print">
          <span className={`fund-print-actual v-${e.card.verdict}`}>
            {e.card.actual}{e.card.unit === '%' ? '%' : e.card.unit ? ` ${e.card.unit}` : ''}
          </span>
          <span className="fund-vs">
            {e.card.consensus === null
              ? 'no forecast published'
              : `vs ${e.card.consensus} expected`}
            {e.card.prior !== null ? ` · ${e.card.prior} prior` : ''}
          </span>
          {e.card.verdict !== 'in_line' ? (
            <span className={`fund-verdict-badge ${e.card.verdict}`}>
              {e.card.verdict === 'above' ? 'ABOVE' : 'BELOW'}
            </span>
          ) : null}
        </div>
      ) : null}

      {e.kind === 'item' && e.item.summary ? (
        <p className="fund-summary">{e.item.summary}</p>
      ) : null}

      {e.kind === 'item' ? (
        <div className="fund-meta">
          <Pips sources={[e.sourceId]} />
          <span className="fund-corro-text">only source</span>
        </div>
      ) : null}

      <Chips affects={e.affects} traded={traded} />

      {e.kind === 'upcoming' && !e.up.enabled ? (
        // A countdown to something nobody is watching is worse than no
        // countdown: it promises a number that will never arrive.
        <p className="fund-warn">Not being watched — this source has no API key configured.</p>
      ) : null}

      {e.kind !== 'upcoming' && e.why && e.score !== undefined
        ? <WhyHere why={e.why} score={e.score} /> : null}
    </li>
  );
}

type SpineFilter = 'all' | 'mine' | 'releases';

/**
 * The whole spine: scheduled above the marker, published below it.
 *
 * The marker is a rendered row rather than a border on the first published
 * entry, because it has to survive filtering — hiding every scheduled entry
 * must not silently remove the boundary and leave the list looking like it is
 * all in the past.
 */
function Spine(
  { cal, feed, zone, traded }:
  { cal: CalendarResponse | null; feed: NewsFeedResponse | null;
    zone: string | undefined; traded: Set<string> },
): JSX.Element {
  const [filter, setFilter] = useState<SpineFilter>('all');
  // One timer for every countdown on the screen. A second is the right
  // resolution: this counts down to a release, not a race start.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => { setNow(Date.now()); }, 1000);
    return () => { clearInterval(t); };
  }, []);

  const rows = useMemo(
    () => buildTimeline(spineEntries(cal, feed), { now: new Date(now), traded }),
    // `now` ticks every second and re-sorting on each tick is wasted work, so
    // the ordering is rebuilt only when the DATA changes. Countdowns still
    // update every second because they read `now` at render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cal, feed, traded],
  );

  const keep = (r: TimelineRow<SpineEntry>): boolean => (
    filter === 'all' ? true
      : filter === 'mine' ? r.mine
        : r.entry.kind !== 'item'
  );

  const shown = rows.filter(keep);
  const scheduled = shown.filter((r) => r.side === 'scheduled');
  const published = shown.filter((r) => r.side === 'published');

  const label = (f: SpineFilter, text: string): JSX.Element => (
    <button
      type="button" className={`chip-btn${filter === f ? ' on' : ''}`}
      aria-pressed={filter === f} onClick={() => { setFilter(f); }}
    >{text}</button>
  );

  return (
    <section className="fund-spine" aria-label="Timeline">
      <div className="fund-filters" role="group" aria-label="Filter the timeline">
        {label('all', 'All')}
        {label('mine', 'My book')}
        {label('releases', 'Releases')}
        <span className="fund-count">
          {countBy(rows, keep)} {countBy(rows, keep) === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      {shown.length === 0 ? (
        <p className="dim">
          {filter === 'mine'
            ? 'Nothing here touches what you trade. That is a real answer, not an empty screen.'
            : 'Nothing yet. Sources fill in on their first poll.'}
        </p>
      ) : null}

      <ol className="fund-rows">
        {scheduled.map((r) => (
          <SpineRow key={keyOf(r)} row={r} now={now} zone={zone} traded={traded} />
        ))}

        {shown.length > 0 ? (
          <li className="fund-now" aria-label="now">
            <span className="fund-now-label">NOW</span>
            <span className="fund-now-rule" aria-hidden="true" />
          </li>
        ) : null}

        {published.map((r) => (
          <SpineRow key={keyOf(r)} row={r} now={now} zone={zone} traded={traded} />
        ))}
      </ol>
    </section>
  );
}

/** Stable across re-sorts — the source and instant identify the entry. */
function keyOf(r: TimelineRow<SpineEntry>): string {
  const e = r.entry;
  if (e.kind === 'item') return `i:${e.item.id}`;
  if (e.kind === 'release') return `r:${e.card.sourceId}:${e.card.referencePeriod}`;
  return `u:${e.up.sourceId}:${e.up.at}`;
}
// ---------------------------------------------------------------------------

export function FundamentalsScreen(): JSX.Element {
  const [cal, setCal] = useState<CalendarResponse | null>(null);
  const [feed, setFeed] = useState<NewsFeedResponse | null>(null);
  const [reading, setReading] = useState<SentimentReading | null>(null);
  const [zonePref, setZonePref] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * Each effect owns its own liveness flag.
   *
   * A single `useRef(true)` shared across all of them looks tidier and is
   * broken under StrictMode: the first cleanup sets it false, nothing sets it
   * back on the second mount, and every subsequent `setState` is silently
   * discarded. The screen then renders its empty state over a response that
   * arrived with a 200 — which is exactly what it did, and nothing in the
   * console said so.
   */
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        // Ranked, always. The spine's own order is chronological, so the
        // ranking is only used for the score breakdown a row can explain —
        // there is no second ordering for a user to be confused by.
        const r = await api.news({ limit: 60, order: 'ranked' });
        if (live) setFeed(r);
      } catch (e) {
        if (live) setError(e instanceof ApiError ? e.message : 'Could not load the feed.');
      }
    })();
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const [c, p] = await Promise.all([
          api.newsCalendar({ days: 21 }),
          // A missing preference is not a reason to fail the screen; the
          // countdown falls back to the browser's own zone.
          api.preferences().catch(() => null),
        ]);
        if (!live) return;
        setCal(c);
        setZonePref(p?.timezone ?? null);
      } catch (e) {
        if (live) setError(e instanceof ApiError ? e.message : 'Could not load the calendar.');
      }
    })();
    return () => { live = false; };
  }, []);

  /*
   * What this trader actually trades, for the relevance rail.
   *
   * Derived from the ranked feed rather than fetched. The server already
   * decided relevance per entry — `explainScore` emits a `relevance` term
   * naming the matched symbols — so the answer is in a response the screen has
   * to make anyway. A second endpoint would be a second opinion about the same
   * question, free to disagree with the ranking the rail sits next to.
   *
   * Empty for an account with no journal, which is the honest state: the rail
   * appears on nothing rather than on everything.
   */
  const traded = useMemo(() => {
    const out = new Set<string>();
    if (feed?.personalised !== true) return out;
    for (const e of feed.entries) {
      const rel = e.why?.find((t) => t.id === 'relevance');
      if (!rel || rel.value <= 0) continue;
      const affects = e.kind === 'release' ? (e.card.affects ?? []) : e.item.affects;
      for (const a of affects) {
        // The detail line names the matched symbols in the user's own terms.
        if (rel.detail.toUpperCase().includes(a.toUpperCase())) out.add(a.toUpperCase());
      }
    }
    return out;
  }, [feed]);

  // The reading is the only part that has to feel alive. Fifteen seconds is
  // frequent enough to move with the market and slow enough that it is not a
  // request per second per open tab.
  useEffect(() => {
    let live = true;
    const tick = async (): Promise<void> => {
      try {
        const r = await api.sentiment();
        if (live) setReading(r);
      } catch { /* a failed poll is not worth an error bar; the next one retries */ }
    };
    void tick();
    const t = setInterval(() => { void tick(); }, 15_000);
    return () => { live = false; clearInterval(t); };
  }, []);

  const zone = useMemo(() => zoneOf(zonePref), [zonePref]);

  return (
    <div className="scroll-area fund">
      <div className="section-head">
        <h2>Fundamentals</h2>
        <span className="fund-zone">times in {zoneLabel(zone)}</span>
      </div>
      <p className="panel-body fund-intro">
        What is scheduled, what the market is doing, and what has been published. AEGIS
        reports these; it does not tell you what to do about them.
      </p>

      {error ? <p className="fund-warn">{error}</p> : null}

      <SessionInstrument reading={reading} feed={feed} zone={zone} />
      <Spine cal={cal} feed={feed} zone={zone} traded={traded} />
    </div>
  );
}
